import { Box, Center, Flex, VStack } from '@chakra-ui/react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import type { leagueMember } from '@/models/user';
import { LeaderboardRow, type LeaderboardMemberRow } from './LeaderboardRow';
import { LeaderboardSkeletonRow } from './LeaderboardSkeletonRow';

const INITIAL_SKELETON_COUNT = 8;

export function LeaderboardList(props: {
  rows: LeaderboardMemberRow[] | null;
  readOnly?: boolean;
  maxH?: string;
  onTransferOwnership?: (uid: string) => void;
  onKick?: (uid: string) => void;
  onViewMemberList?: (uid: string, member: leagueMember) => void;
  listRef?: RefObject<HTMLDivElement | null>;
  showExternalScrollbar?: boolean;
  isInitialLoading?: boolean;
  isPageLoading?: boolean;
}) {
  const {
    rows,
    readOnly = false,
    maxH,
    onTransferOwnership,
    onKick,
    onViewMemberList,
    listRef: listRefProp,
    showExternalScrollbar = true,
    isInitialLoading = false,
    isPageLoading = false,
  } = props;

  const scrollable = maxH != null && maxH.length > 0;

  const internalListRef = useRef<HTMLDivElement>(null);
  const listRef = (listRefProp ?? internalListRef) as RefObject<HTMLDivElement>;
  const contentScrollRef = useRef<HTMLDivElement>(null);
  const barScrollRef = useRef<HTMLDivElement>(null);
  const [scrollSpacerHeight, setScrollSpacerHeight] = useState(0);
  const syncingScroll = useRef(false);

  const updateScrollSpacerHeight = useCallback(() => {
    const el = listRef.current;
    if (el) {
      setScrollSpacerHeight(el.scrollHeight);
    }
  }, [listRef]);

  useLayoutEffect(() => {
    updateScrollSpacerHeight();
  }, [rows, isInitialLoading, updateScrollSpacerHeight]);

  useEffect(() => {
    const el = listRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => updateScrollSpacerHeight());
    observer.observe(el);
    return () => observer.disconnect();
  }, [listRef, updateScrollSpacerHeight]);

  const syncScroll = useCallback((source: 'content' | 'bar') => {
    if (syncingScroll.current) return;
    syncingScroll.current = true;
    const from = source === 'content' ? contentScrollRef.current : barScrollRef.current;
    const to = source === 'content' ? barScrollRef.current : contentScrollRef.current;
    if (from && to) {
      to.scrollTop = from.scrollTop;
    }
    syncingScroll.current = false;
  }, []);

  const useScrollbarStrip = scrollable && showExternalScrollbar && !readOnly;

  const listBody = (
    <VStack
      ref={listRef}
      align='stretch'
      spacing={1}
      w='full'
      opacity={isPageLoading ? 0.55 : 1}
      transition='opacity 0.15s ease-out'
    >
      {isInitialLoading ? (
        Array.from({ length: INITIAL_SKELETON_COUNT }, (_, i) => <LeaderboardSkeletonRow key={`sk-${i}`} />)
      ) : rows && rows.length > 0 ? (
        rows.map((row) => (
          <LeaderboardRow
            key={row.memberUid}
            {...row}
            readOnly={readOnly}
            onTransferOwnership={onTransferOwnership}
            onKick={onKick}
            onViewMemberList={onViewMemberList}
          />
        ))
      ) : (
        <Center py={8} fontSize='sm' color='gray.500'>
          No members yet
        </Center>
      )}
    </VStack>
  );

  if (!scrollable) {
    return <Box w='full'>{listBody}</Box>;
  }

  return (
    <Flex flex={1} minH={0} w='full' align='stretch' gap={3} maxH={maxH}>
      <Box flex={1} minW={0} minH={0} overflow='hidden'>
        <Box
          ref={contentScrollRef}
          h='full'
          maxH={maxH}
          overflowY='auto'
          overflowX='hidden'
          onScroll={useScrollbarStrip ? () => syncScroll('content') : undefined}
          sx={
            useScrollbarStrip
              ? {
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                  '&::-webkit-scrollbar': { display: 'none' },
                }
              : undefined
          }
        >
          {listBody}
        </Box>
      </Box>
      {useScrollbarStrip ? (
        <Box
          ref={barScrollRef}
          w={2}
          flexShrink={0}
          overflowY='auto'
          overflowX='hidden'
          onScroll={() => syncScroll('bar')}
          aria-hidden
          tabIndex={-1}
          sx={{
            '&::-webkit-scrollbar': { width: '6px' },
            '&::-webkit-scrollbar-thumb': {
              borderRadius: 'full',
              bg: 'blackAlpha.300',
            },
            _dark: {
              '&::-webkit-scrollbar-thumb': { bg: 'whiteAlpha.300' },
            },
          }}
        >
          <Box h={`${scrollSpacerHeight}px`} w='1px' />
        </Box>
      ) : null}
    </Flex>
  );
}
